import { Fragment, memo, useState } from 'react';
import Checkbox from '../Checkbox.jsx';
import Skeleton from '../Skeleton.jsx';
import DataTableTruncatedText from './DataTableTruncatedText.jsx';
import { ChevronDown } from 'lucide-react';

const VARIANT_STYLES = {
  default: {
    table: 'w-full text-left',
    thead: 'sticky top-0 z-[1] bg-surface-muted',
    theadRow: 'border-b border-edge',
    th: 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-content-muted',
    td: 'px-4 py-3 align-middle text-sm',
    tbody: 'divide-y divide-edge bg-surface',
    skeletonHeight: '44px',
    skeletonPerCell: false,
  },
  /** Figma Agentic Networks — Log table (node 47-948). */
  workspace: {
    table: 'w-full table-fixed border-collapse text-left',
    thead:
      'sticky top-0 z-[1] bg-[#f6f9ff] shadow-[inset_0_-1px_0_rgba(0,0,0,0.12)] dark:bg-surface-muted dark:shadow-none',
    theadRow: '',
    th: 'h-[60px] px-2 text-left text-[14px] font-semibold leading-[20px] text-content',
    td: 'h-[60px] px-2 align-middle',
    tbody: 'bg-surface',
    skeletonHeight: 20,
    skeletonPerCell: true,
  },
};

const SKELETON_ROW_COUNT = 8;

const joinClasses = (...parts) => parts.filter(Boolean).join(' ');

const resolveTooltipText = (col, raw, row, content) => {
  const fromGetter = col.getTooltipText?.(raw, row);
  if (fromGetter != null && fromGetter !== '') return String(fromGetter);
  if (typeof content === 'string' || typeof content === 'number') return String(content);
  if (typeof raw === 'string' || typeof raw === 'number') return String(raw);
  return '';
};

/** Wraps cell content with single-line ellipsis and an optional hover/focus tooltip. */
const DataTableCellContent = memo(function DataTableCellContent({ col, raw, row }) {
  const content = col.render ? col.render(raw, row) : raw;

  if (!col.truncate) return content;

  const tooltipText = resolveTooltipText(col, raw, row, content);
  const isPlainText = typeof content === 'string' || typeof content === 'number';

  if (isPlainText) {
    return (
      <DataTableTruncatedText tooltipText={tooltipText || undefined}>
        {content}
      </DataTableTruncatedText>
    );
  }

  return <div className="min-w-0 w-full overflow-hidden">{content}</div>;
});

/**
 * Dense list table (sticky header, workspace-style cells). For sortable grids with chevrons, use {@link Table}.
 * Optional leading checkbox column when `selection.enabled` is true.
 *
 * Column overflow: set `truncate: true` for single-line ellipsis. Pass `getTooltipText(value, row)`
 * (or a string cell value) to show the full text in a tooltip on hover/focus when the text
 * overflows (ellipsis). Custom renders with icons should use {@link DataTableTruncatedText}
 * for the text segment and pass `tooltipText` with the full string.
 *
 * Expandable rows: pass `renderRowDetail(row)` to add a trailing chevron column whose click
 * reveals a full-width detail row beneath. Expansion is uncontrolled (internal state, keyed by
 * `getRowId`), rows toggle independently, and clicking anywhere on a non-interactive part of a
 * row toggles it too — unless `onRowClick` is set, which takes precedence for the row body.
 *
 * Style overrides: `classNames` is a per-slot map appended on top of the variant styles
 * (`table`, `thead`, `theadRow`, `th`, `td`, `tbody`, `row`, `detailCell`, `emptyCell`) — no
 * `!important` needed. `rowClassName(row)` adds data-driven classes to each body row (e.g. to
 * tint a `crit` severity). Both compose with per-column `headerClassName`/`cellClassName`.
 *
 * @param {object} props
 * @param {'default'|'workspace'} [props.variant='default'] — `workspace` matches Figma Agentic Networks Log (47-948).
 * @param {Array<{
 *   key: string,
 *   label: import('react').ReactNode,
 *   headerClassName?: string,
 *   cellClassName?: string,
 *   render?: (value: unknown, row: object) => import('react').ReactNode,
 *   truncate?: boolean,
 *   getTooltipText?: (value: unknown, row: object) => string | null | undefined,
 * }>} props.columns
 * @param {object[]} props.data
 * @param {boolean} [props.loading=false]
 * @param {number|string} [props.minTableWidth=720]
 * @param {object} [props.selection]
 * @param {string} [props.emptyMessage]
 * @param {(row: object) => void} [props.onRowClick]
 * @param {(row: object) => import('react').ReactNode} [props.renderRowDetail] — enables expandable rows.
 * @param {(row: object) => (string|number)} [props.getRowId] — row identity for selection/expansion (default `row.id`).
 * @param {string} [props.expandRowAriaLabel='Expand row'] — aria-label for the expand toggle.
 * @param {{
 *   table?: string, thead?: string, theadRow?: string, th?: string, td?: string,
 *   tbody?: string, row?: string, detailCell?: string, emptyCell?: string,
 * }} [props.classNames] — per-slot class overrides, appended on top of the variant.
 * @param {(row: object) => string} [props.rowClassName] — extra classes per body row (data-driven).
 */
const DataTable = ({
  columns,
  data = [],
  loading = false,
  minTableWidth = 720,
  selection,
  emptyMessage,
  variant = 'default',
  onRowClick,
  renderRowDetail,
  getRowId: getRowIdProp,
  expandRowAriaLabel = 'Expand row',
  classNames = {},
  rowClassName,
}) => {
  const v = VARIANT_STYLES[variant] ?? VARIANT_STYLES.default;
  const selectionEnabled = Boolean(selection?.enabled);
  const expandable = typeof renderRowDetail === 'function';
  const colCount = columns.length + (selectionEnabled ? 1 : 0) + (expandable ? 1 : 0);
  const rowClickable = typeof onRowClick === 'function';

  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const toMinWidthStyle = (value) => {
    if (typeof value === 'number') return { minWidth: `${value}px` };
    if (typeof value === 'string' && value) return { minWidth: value };
    return undefined;
  };
  const minWidthStyle = toMinWidthStyle(minTableWidth);

  const getRowId = getRowIdProp ?? selection?.getRowId ?? ((row) => row.id);
  // Body-cell base shared by data, selection and expand cells (variant + `classNames.td`).
  const tdClass = joinClasses(v.td, classNames.td);

  const toggleRow = (id) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handleRowClick = (event, row) => {
    if (event.target.closest('button, input, a, [role="checkbox"]')) return;
    if (rowClickable) {
      onRowClick(row);
      return;
    }
    if (expandable) toggleRow(getRowId(row));
  };

  const renderExpandCell = (rowId, expanded) => (
    <td className={`${tdClass} w-10`}>
      <button
        type="button"
        onClick={() => toggleRow(rowId)}
        aria-label={expandRowAriaLabel}
        aria-expanded={expanded}
        className="flex h-6 w-6 items-center justify-center rounded text-content-muted transition-colors hover:text-content"
      >
        <ChevronDown
          size={16}
          className={`transition-transform ${expanded ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>
    </td>
  );

  const renderBody = () => {
    if (loading) {
      return Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
        <tr key={`datatable-skel-${i}`}>
          {selectionEnabled && v.skeletonPerCell ? (
            <td className={`${tdClass} w-14`}>
              <Skeleton height={v.skeletonHeight} borderRadius={6} />
            </td>
          ) : null}
          {v.skeletonPerCell ? (
            <>
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={joinClasses(tdClass, col.cellClassName, col.truncate && 'max-w-0')}
                >
                  <Skeleton height={v.skeletonHeight} borderRadius={6} />
                </td>
              ))}
              {expandable ? <td className={`${tdClass} w-10`} /> : null}
            </>
          ) : (
            <td colSpan={colCount} className={`${tdClass} py-2`}>
              <Skeleton height={v.skeletonHeight} width="100%" borderRadius="6px" />
            </td>
          )}
        </tr>
      ));
    }

    if (!data.length) {
      if (emptyMessage == null || emptyMessage === '') {
        return [];
      }
      return (
        <tr>
          <td
            colSpan={colCount}
            className={joinClasses(v.td, 'py-12 text-center text-content-faint', classNames.emptyCell)}
          >
            {emptyMessage}
          </td>
        </tr>
      );
    }

    return data.map((row, rowIdx) => {
      const rowId = getRowId(row);
      const key = rowId != null ? String(rowId) : `row-${rowIdx}`;
      const selected = selectionEnabled && selection.isRowSelected?.(row);
      const expanded = expandable && expandedIds.has(rowId);
      const rowInteractive = rowClickable || expandable;

      return (
        <Fragment key={key}>
          <tr
            className={[
              'transition-colors hover:bg-surface-hover-subtle',
              selected ? 'bg-[#E9F1FF]/70 dark:bg-blue-950/30' : '',
              expanded ? 'bg-surface-hover-subtle' : '',
              rowInteractive ? 'cursor-pointer' : '',
              classNames.row,
              typeof rowClassName === 'function' ? rowClassName(row) : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={rowInteractive ? (event) => handleRowClick(event, row) : undefined}
          >
            {selectionEnabled ? (
              <td className={`${tdClass} w-14`} onClick={(event) => event.stopPropagation()}>
                <Checkbox
                  size="small"
                  checked={Boolean(selected)}
                  onChange={() => selection.onToggleRow?.(row)}
                  aria-label={selection.rowSelectAriaLabel?.(row)}
                />
              </td>
            ) : null}
            {columns.map((col) => {
              const raw = row[col.key];
              return (
                <td
                  key={col.key}
                  className={joinClasses(tdClass, col.cellClassName, col.truncate && 'max-w-0')}
                >
                  <DataTableCellContent col={col} raw={raw} row={row} />
                </td>
              );
            })}
            {expandable ? renderExpandCell(rowId, expanded) : null}
          </tr>
          {expanded ? (
            <tr className={v.tbody.includes('bg-surface') ? 'bg-surface' : undefined}>
              <td colSpan={colCount} className={joinClasses('px-2 pb-4 pt-0', classNames.detailCell)}>
                {renderRowDetail(row)}
              </td>
            </tr>
          ) : null}
        </Fragment>
      );
    });
  };

  const thClass = joinClasses(v.th, classNames.th);

  return (
    <table className={joinClasses(v.table, classNames.table)} style={minWidthStyle}>
      <thead className={joinClasses(v.thead, classNames.thead)}>
        <tr className={joinClasses(v.theadRow, classNames.theadRow)}>
          {selectionEnabled ? (
            <th scope="col" className={`${thClass} w-14`}>
              <Checkbox
                size="small"
                checked={Boolean(selection.allSelected)}
                disabled={Boolean(selection.selectAllDisabled)}
                onChange={() => selection.onToggleAll?.()}
                aria-label={selection.selectAllAriaLabel}
              />
            </th>
          ) : null}
          {columns.map((col) => (
            <th
              key={col.key}
              scope="col"
              className={joinClasses(thClass, col.headerClassName)}
            >
              {col.label}
            </th>
          ))}
          {expandable ? <th scope="col" className={`${thClass} w-10`} aria-label={expandRowAriaLabel} /> : null}
        </tr>
      </thead>
      <tbody className={joinClasses(v.tbody, classNames.tbody)}>{renderBody()}</tbody>
    </table>
  );
};

DataTable.TruncatedText = DataTableTruncatedText;

export default DataTable;
